"use client";

import cn from "@utils/cn";
import React, { FC, useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { Mousewheel, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "@/components/HeroCarousel/HeroCarousel.css";
import DynamicImport from "@/components/dynamicImport";
import useMediaQuery from "@/hooks/useMediaQuery";

import { HomePageHeroCarouselProps } from "./types";

SwiperCore.use([Mousewheel, Pagination]);

const HomePageHeroCarousel: FC<HomePageHeroCarouselProps> = ({ entry }) => {
  const { heroSlides } = entry;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const swiperRef = useRef<SwiperCore>();
  const container = useRef<HTMLDivElement>(null);
  const [isAfterVisible, setAfterVisible] = useState(false);
  const singleSlide = heroSlides.length === 1;

  const handleScroll = () => {
    if (entry.animation) {
      const heroHeight = window.innerHeight;
      const scrollPosition = window.scrollY || window.pageYOffset;

      if (scrollPosition >= heroHeight / (!isMobile || singleSlide ? 1.1 : 1.75)) {
        setAfterVisible(true);
      } else {
        setAfterVisible(false);
      }
    }
  };

  useEffect(() => {
    // At component loading reset scroll to 0
    if (entry.animation) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (isDesktop && event.deltaY < 0 && !swiperRef.current?.mousewheel.enabled) {
      swiperRef.current?.mousewheel.enable();
    }
  };

  const getSwiperBreakpoints = (isMultiSlide: boolean) => {
    const multiSlideBreakpoints = {
      1024: {
        direction: "vertical",
        mousewheel: {
          enabled: true,
          eventsTarget: "#homepage-hero-carousel",
          thresholdDelta: 10,
        },
      },
    };
    return isMultiSlide ? multiSlideBreakpoints : {};
  };

  const getSiblings = (elm: any) => elm && elm.parentNode && [...elm.parentNode.children].filter((node) => node !== elm);

  useEffect(() => {
    if (container.current && entry.animation) {
      let siblings = getSiblings(container.current);

      siblings.forEach((sibling: HTMLElement) => {
        sibling.style.backgroundColor = "white";
        sibling.style.zIndex = "10";
      });

      if (siblings instanceof HTMLTemplateElement) {
        const templateContent = siblings.content;

        templateContent.querySelectorAll("div").forEach((divInTemplate) => {
          divInTemplate.style.backgroundColor = "white";
          divInTemplate.style.zIndex = "10";
        });
      }
    }
  }, [container.current, entry.animation]);

  return (
    <div
      className={cn("w-full h-svh", entry.animation && "sticky top-0 left-0 !z-[1] is-sticky", isAfterVisible && "after-visible")}
      id="homepage-hero-carousel"
      onWheel={handleWheel}
      ref={container}
    >
      <Swiper
        className="w-full h-full"
        speed={1500}
        breakpoints={getSwiperBreakpoints(heroSlides.length > 1)}
        onSwiper={(swiper) => heroSlides.length > 1 && (swiperRef.current = swiper)}
        onTransitionStart={(swiper) => {
          if (!(swiper as SwiperCore).isEnd) {
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
          }
        }}
        onTransitionEnd={(swiper) => {
          if (isDesktop) {
            if (swiper.isEnd) {
              swiper.mousewheel.disable();
            }
          }
        }}
        simulateTouch={false}
        pagination={isDesktop ? false : { type: "bullets", clickable: true }}
      >
        {heroSlides?.map((slide, i) => (
          <SwiperSlide key={i}>
            <DynamicImport components={[slide]} h1ID={i === 0 && slide.id} animation={entry.animation} singleSlide={singleSlide} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};
export default HomePageHeroCarousel;
